import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { TrendingUp, Link2, IndianRupee, BarChart3, Shield, UserPlus, ArrowRight, ArrowLeft, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const fadeUp = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } };
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

const benefits = [
  { icon: <Link2 className="w-5 h-5" />, title: "Unique Referral Link", desc: "Get your own branded link to share with your audience." },
  { icon: <IndianRupee className="w-5 h-5" />, title: "Earn Per Sale", desc: "Commission on every successful paid reading through your link." },
  { icon: <BarChart3 className="w-5 h-5" />, title: "Live Dashboard", desc: "Track clicks, conversions, earnings and payouts in real time." },
  { icon: <Shield className="w-5 h-5" />, title: "Reliable Payouts", desc: "Transparent commission tracking with manual UPI/bank payouts." },
];

const steps = [
  { num: "1", title: "Register", desc: "Fill out a quick application form." },
  { num: "2", title: "Get Approved", desc: "Our team reviews and activates your account." },
  { num: "3", title: "Share Your Link", desc: "Promote VishuAstro with your unique referral link." },
  { num: "4", title: "Earn Commission", desc: "Get paid for every successful customer payment." },
];

export default function AffiliatePage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Minimal nav */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-xl border-b border-gray-100 px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.jpg" alt="Vishu Astro" className="w-8 h-8 rounded-full object-cover" />
            <span className="font-bold text-gray-900 text-sm" style={{ fontFamily: 'Space Grotesk' }}>Vishu Astro</span>
          </Link>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => navigate("/influencer/login")} className="text-xs text-gray-500 hover:text-gray-900" data-testid="aff-page-login-btn">
              Login
            </Button>
            <Button onClick={() => navigate("/influencer/register")} className="bg-gray-900 text-white text-xs rounded-xl px-4 hover:bg-gray-800" data-testid="aff-page-register-btn">
              Join Now
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="px-4 pt-16 pb-12 sm:pt-20 sm:pb-16">
        <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-2xl mx-auto text-center">
          <motion.div variants={fadeUp} className="inline-flex items-center gap-1.5 bg-amber-50 text-amber-700 text-xs font-semibold px-3 py-1.5 rounded-full mb-6 border border-amber-100">
            <TrendingUp className="w-3.5 h-3.5" /> Affiliate Program
          </motion.div>
          <motion.h1 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-gray-900 leading-tight mb-4" style={{ fontFamily: 'Space Grotesk' }}>
            Earn money by sharing<br />
            <span className="text-amber-500">VishuAstro</span> with your audience
          </motion.h1>
          <motion.p variants={fadeUp} className="text-gray-500 text-base sm:text-lg mb-8 max-w-lg mx-auto leading-relaxed">
            Join our affiliate program, get a unique referral link, and earn commission on every paid consultation your audience completes.
          </motion.p>
          <motion.div variants={fadeUp} className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button onClick={() => navigate("/influencer/register")}
              className="bg-gradient-to-r from-amber-400 to-amber-600 text-gray-900 font-bold rounded-2xl px-8 py-6 text-base shadow-md hover:shadow-lg hover:from-amber-500 hover:to-amber-700 transition-all"
              data-testid="aff-hero-register-btn">
              <UserPlus className="w-5 h-5 mr-2" /> Apply Now
            </Button>
            <Button variant="outline" onClick={() => navigate("/influencer/login")}
              className="rounded-2xl px-8 py-6 text-base border-gray-200 text-gray-600 hover:bg-gray-50"
              data-testid="aff-hero-login-btn">
              Already a partner? Login <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </motion.div>
        </motion.div>
      </section>

      {/* Benefits */}
      <section className="px-4 py-12 bg-gray-50/60">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900 text-center mb-8" style={{ fontFamily: 'Space Grotesk' }}>Why partner with us?</h2>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
            className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {benefits.map((b, i) => (
              <motion.div key={i} variants={fadeUp}
                className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm hover:shadow-md transition-shadow" data-testid={`aff-benefit-${i}`}>
                <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center mb-3">{b.icon}</div>
                <h3 className="font-bold text-gray-900 text-sm mb-1" style={{ fontFamily: 'Space Grotesk' }}>{b.title}</h3>
                <p className="text-gray-500 text-xs leading-relaxed">{b.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* How it works */}
      <section className="px-4 py-12">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900 text-center mb-8" style={{ fontFamily: 'Space Grotesk' }}>How it works</h2>
          <div className="space-y-4">
            {steps.map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, x: -12 }} whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="flex items-start gap-4" data-testid={`aff-step-${i}`}>
                <div className="w-9 h-9 rounded-full bg-gray-900 text-white flex items-center justify-center font-bold text-sm shrink-0" style={{ fontFamily: 'Space Grotesk' }}>
                  {s.num}
                </div>
                <div className="pt-1">
                  <h3 className="font-bold text-gray-900 text-sm" style={{ fontFamily: 'Space Grotesk' }}>{s.title}</h3>
                  <p className="text-gray-500 text-xs mt-0.5">{s.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Quick highlights */}
      <section className="px-4 py-10 bg-gray-50/60">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
            <h3 className="font-bold text-gray-900 text-sm mb-4" style={{ fontFamily: 'Space Grotesk' }}>Program Highlights</h3>
            <div className="space-y-2.5">
              {[
                "Competitive commission on every successful payment",
                "Real-time tracking dashboard with detailed analytics",
                "No minimum follower count required",
                "Payouts via UPI or bank transfer",
                "Dedicated support for affiliate partners",
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                  <span className="text-gray-600 text-xs">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 py-14">
        <div className="max-w-lg mx-auto text-center">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'Space Grotesk' }}>Ready to start earning?</h2>
          <p className="text-gray-500 text-sm mb-6">Apply now and start earning from your very first referral.</p>
          <Button onClick={() => navigate("/influencer/register")}
            className="bg-gradient-to-r from-amber-400 to-amber-600 text-gray-900 font-bold rounded-2xl px-10 py-6 text-base shadow-md hover:shadow-lg transition-all"
            data-testid="aff-bottom-cta-btn">
            <UserPlus className="w-5 h-5 mr-2" /> Apply Now — It&apos;s Free
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-100 py-8 px-4 bg-white">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.jpg" alt="Vishu Astro" className="w-6 h-6 rounded-full object-cover" />
            <span className="font-semibold text-gray-700 text-xs" style={{ fontFamily: 'Space Grotesk' }}>Vishu Astro</span>
          </Link>
          <div className="flex items-center gap-4 text-xs text-gray-400">
            <Link to="/legal/privacy" className="hover:text-gray-700 transition-colors">Privacy</Link>
            <Link to="/legal/terms" className="hover:text-gray-700 transition-colors">Terms</Link>
            <Link to="/legal/contact" className="hover:text-gray-700 transition-colors">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
